/**
 * Types for the bulk exclusion reconcile.
 *
 * "Exclusion" is not an entity - it is a family of menu patch operations. A search asks one question
 * about one target across many stores: does that store's own patch carry this operation?
 */

/** The operation types that count as exclusions, across the two exclusion patch types. */
export const EXCLUSION_OPERATION_TYPES = [
  'RemoveProductMenuPatchOperation',
  'RemoveVariantMenuPatchOperation',
  'RemoveModifierMenuPatchOperation',
  'RemoveModifierFromSlotPatchOperation',
  'RemoveWeightFromSlotModifierMenuPatchOperation',
  'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
  'RemoveBundleMenuPatchOperation',
] as const;

export type ExclusionOperationType = typeof EXCLUSION_OPERATION_TYPES[number];

/**
 * What one store's patch says about the target.
 *
 * Three values, not two. `explicitlyNotExcluded` only arises for
 * SetModifierWeightIsExcludedFromVariant, which stores a boolean rather than expressing exclusion by
 * existing: a stored `false` is somebody's deliberate un-exclude, and treating it as `absent` would both
 * hide that decision and leave a bulk remove with nothing to act on.
 */
export type ExclusionState = 'absent' | 'excluded' | 'explicitly_not_excluded';

/**
 * The single menu node a search asks about.
 *
 * Which ids are required depends on the operation type - see EXCLUSION_TARGET_REQUIRED_IDS. The backend
 * validates the same rule and rejects an incomplete target, so this is for building the request, not for
 * trusting it.
 */
export interface ExclusionTarget {
  operationType: ExclusionOperationType;
  productId?: string;
  variantId?: string;
  slotId?: string;
  modifierId?: string;
  weightId?: string;
  bundleId?: string;
}

/** Which target ids identify each operation type. Mirrors the backend's exclusionOperationSpecs. */
export const EXCLUSION_TARGET_REQUIRED_IDS: Record<ExclusionOperationType, Array<keyof ExclusionTarget>> = {
  RemoveProductMenuPatchOperation: ['productId'],
  RemoveVariantMenuPatchOperation: ['variantId'],
  RemoveModifierMenuPatchOperation: ['modifierId'],
  RemoveModifierFromSlotPatchOperation: ['slotId', 'modifierId'],
  RemoveWeightFromSlotModifierMenuPatchOperation: ['slotId', 'modifierId', 'weightId'],
  SetModifierWeightIsExcludedFromVariantMenuPatchOperation: ['variantId', 'slotId', 'modifierId', 'weightId'],
  RemoveBundleMenuPatchOperation: ['bundleId'],
};

/**
 * One store's patch, as the caller already knows it.
 *
 * The frontend has this pairing exactly: the store payload it loads carries
 * assignedMenuDefinitions[].patches[].patch, and the store number comes from the store object rather than
 * from parsing a patch name. Sending it means the backend attributes results by patchId instead of
 * re-deriving a store number from a naming convention that holds for most patches but not all.
 */
export interface ExclusionPatchRef {
  storeNumber: string;
  patchId: string;
}

/** One store's answer. */
export interface ExclusionSearchResult {
  storeNumber: string;
  patchId: string;
  state: ExclusionState;
  /** Absent when state is `absent`. Not needed to remove - removal is by entity reference. */
  patchOperationId?: string;
  /**
   * Other stores this patch is assigned to that were not searched.
   *
   * Operations belong to the patch and the patch is assigned to store-channels, so staging a change for
   * this store changes these too. In staging every store patch is shared this way, across three or four
   * stores - so this is the common case, not an oddity. Surfaced because the alternative is a user
   * discovering it afterwards.
   */
  otherStoreNumbers?: string[];
  /**
   * The backend's assignment page was full, so `otherStoreNumbers` may be short. Lets the UI say "at
   * least N" rather than implying the list is complete.
   */
  otherStoresTruncated?: boolean;
}

export interface ExclusionSearchResponse {
  results: ExclusionSearchResult[];
  /**
   * Patches that were asked for but did not come back - most likely a patch whose name does not carry the
   * type suffix the query narrows on. Reported rather than treated as `absent`, which would be a claim
   * about the patch's contents rather than an admission we could not read it.
   */
  unreadable: string[];
  /**
   * Stores whose batch failed upstream. Nothing was learned about them either way, so they are neither
   * excluded nor absent, and the user can re-run the search for just these.
   */
  failed: string[];
}

export interface ExclusionSearchRequest {
  /**
   * The stores to search. The backend resolves each store's patch from the type suffix in its name.
   *
   * Store numbers rather than patch ids: the frontend's own view of a store's patches comes from the store
   * payload, whose patch list is scoped to the ECOMM menu channel, so a patch assigned elsewhere is
   * invisible to it. Resolving server-side sees all of them.
   */
  storeNumbers: string[];
  target: ExclusionTarget;
  /** Optional override, for narrowing batches while diagnosing an upstream limit. */
  batchSize?: number;
}

/** The target ids this operation type needs but the target does not carry. */
export const missingTargetIds = (target: ExclusionTarget): Array<keyof ExclusionTarget> => {
  const required = EXCLUSION_TARGET_REQUIRED_IDS[target.operationType];
  if (!required) return [];

  return required.filter((field) => !target[field]);
};

/** Whether a target is complete enough to search with. */
export const isTargetComplete = (target: ExclusionTarget | null): target is ExclusionTarget =>
  !!target && missingTargetIds(target).length === 0;
